/**
 * Finding the rows a link row question is prefilled with.
 *
 * The one prefill that cannot be read straight off the address bar. Every other
 * question's draft is the text somebody typed, and a url parameter is text; a
 * link row's draft names rows by id, and a link names them by the text of the
 * related table's primary field -- which nobody writing a link could know the
 * id of.
 *
 * So this looks them up, through the same endpoint the question's own search
 * box uses. A name that matches nothing is left out rather than guessed at: a
 * link row cell holds rows, and there is no row to hold for a name that is not
 * one.
 */
import { useQuery } from '@tanstack/react-query'

import { qk } from '@/api/query-keys'
import type { Field } from '@/api/types'
import type { LinkedRow } from '../fields/ui/edit/link-row'
import { linkRowNames, needsLookup, prefillFor } from './prefill'
import type { FormField } from './queries'
import { formSources } from './sources'

/** Nothing to fill in, as one object rather than a new one per render. */
const NONE: Record<number, string> = {}

export function usePrefilledLinks(
  slug: string,
  fields: readonly FormField[],
  prefills: ReadonlyMap<string, string>,
): Record<number, string> {
  const wanted = fields
    .filter(({ field }) => needsLookup(field))
    .map((entry) => ({
      field: entry.field,
      names: linkRowNames(prefillFor(entry, prefills) ?? ''),
    }))
    .filter(({ names }) => names.length > 0)

  const query = useQuery({
    queryKey: qk.formPrefilledLinks(
      slug,
      JSON.stringify(wanted.map(({ field, names }) => [field.id, names])),
    ),
    enabled: wanted.length > 0,
    // Asked once. The names are in the address, so nothing can change them
    // without the page being pointed somewhere else.
    staleTime: Infinity,
    retry: false,
    queryFn: async (): Promise<Record<number, string>> => {
      const { linkCandidates } = formSources(slug)
      const drafts: Record<number, string> = {}

      for (const { field, names } of wanted) {
        const found = await Promise.all(
          names.map((name) => matchByName(linkCandidates, field, name)),
        )
        const rows = found.filter((row): row is LinkedRow => row !== null)
        // Left alone when nothing matched, rather than set to an empty list:
        // an empty draft is an answer, and "nothing here is a row in that
        // table" is not one somebody meant to give.
        if (rows.length > 0) drafts[field.id] = JSON.stringify(rows)
      }

      return drafts
    },
  })

  return query.data ?? NONE
}

/**
 * The row with exactly this name.
 *
 * The endpoint searches rather than looks up, so it answers with everything
 * containing the text -- and a form that prefilled "Acme" with a row called
 * "Acme Holdings" would be quietly answering a different question.
 */
async function matchByName(
  search: (field: Field, term: string) => Promise<LinkedRow[]>,
  field: Field,
  name: string,
): Promise<LinkedRow | null> {
  const candidates = await search(field, name)
  return candidates.find((candidate) => candidate.value === name) ?? null
}
